import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import useGallery from '../hooks/useGallery'
import type { Generation } from '../lib/api'
import GenerationGrid from '../components/GenerationGrid'
import FullscreenModal from '../components/FullscreenModal'
import LoadingSkeleton from '../components/LoadingSkeleton'
import FrankCard from '../components/FrankCard'

type FilterTab = 'all' | 'images' | 'videos'

export default function Gallery() {
  const {
    generations,
    isLoading,
    error,
    hasMore,
    loadMore,
    deleteGeneration,
  } = useGallery()

  const navigate = useNavigate()
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all')
  const [selectedGeneration, setSelectedGeneration] =
    useState<Generation | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const handleReuse = useCallback(
    (generation: Generation) => {
      navigate('/', {
        state: { prompt: generation.prompt, model: generation.model },
      })
    },
    [navigate],
  )

  const filteredGenerations = useMemo(() => {
    if (activeFilter === 'all') return generations
    if (activeFilter === 'images')
      return generations.filter((g) => g.type === 'image')
    return generations.filter((g) => g.type === 'video')
  }, [generations, activeFilter])

  const handleDelete = useCallback(
    async (id: string) => {
      setConfirmDeleteId(null)
      setSelectedGeneration(null)
      await deleteGeneration(id)
    },
    [deleteGeneration],
  )

  const filters: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'images', label: 'Images' },
    { key: 'videos', label: 'Videos' },
  ]

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-xl font-bold text-text-primary">Gallery</h1>

        <div className="flex gap-1 p-1 rounded-lg bg-bg-card border border-border">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer ${
                activeFilter === filter.key
                  ? 'bg-neon text-bg-primary font-semibold'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mx-4 px-4 py-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && generations.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <LoadingSkeleton count={12} />
          </div>
        ) : filteredGenerations.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-3">
              <FrankCard
                src="frank-shrug.png"
                alt="Frank shrugging — nothing here yet"
                badge="/ gallery is empty"
                size="md"
                className="mx-auto"
              />
              <h2 className="text-xl font-semibold text-text-primary">
                {activeFilter === 'all'
                  ? 'No generations yet'
                  : `No ${activeFilter} yet`}
              </h2>
              <p className="text-text-secondary">
                {activeFilter === 'all'
                  ? "Head to Studio to create some!"
                  : "Try generating some in Studio!"}
              </p>
            </div>
          </div>
        ) : (
          <>
            <GenerationGrid
              generations={filteredGenerations}
              onCardClick={setSelectedGeneration}
              onDelete={(id) => setConfirmDeleteId(id)}
              onReuse={handleReuse}
            />

            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={loadMore}
                  className="px-6 py-2.5 text-sm font-medium text-neon border border-neon rounded-lg hover:bg-neon/10 transition-colors cursor-pointer"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedGeneration && (
        <FullscreenModal
          generation={selectedGeneration}
          onClose={() => setSelectedGeneration(null)}
          onDelete={(id) => setConfirmDeleteId(id)}
          onReuse={handleReuse}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/70">
          <div className="bg-bg-card border border-border rounded-xl p-6 max-w-sm mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-text-primary">
              Delete generation?
            </h3>
            <p className="text-sm text-text-secondary">
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors rounded-lg cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDeleteId)}
                className="px-4 py-2 text-sm font-medium bg-error text-white rounded-lg hover:bg-error/80 transition-colors cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
